'use client';

import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { locales, localeMeta } from '@/i18n/locales';
import Flag from '@/components/ui/Flag';
import { cn } from '@/lib/utils';

/**
 * Site-wide language selector (7 flag icons). Stores the choice in the
 * NEXT_LOCALE cookie (read by src/i18n/request.ts) and refreshes so server
 * components re-render in the new locale. No route change, so it works on
 * every page.
 */
export default function LanguageSwitcher({ compact = true }: { compact?: boolean }) {
  const active = useLocale();
  const router = useRouter();
  const [pending, startTransition] = useTransition();

  const change = (code: string) => {
    if (code === active) return;
    // Persist the choice; request.ts reads NEXT_LOCALE, then refresh re-renders in the new locale.
    // eslint-disable-next-line react-hooks/immutability
    document.cookie = `NEXT_LOCALE=${code}; path=/; max-age=31536000; samesite=lax`;
    startTransition(() => router.refresh());
  };

  return (
    <div
      className={cn('flex flex-wrap items-center gap-1.5', pending && 'opacity-60')}
      role="group"
      aria-label="Choose language"
    >
      {locales.map((code) => {
        const isActive = code === active;
        return (
          <button
            key={code}
            type="button"
            onClick={() => change(code)}
            aria-label={localeMeta[code].label}
            aria-current={isActive ? 'true' : undefined}
            title={localeMeta[code].label}
            className={cn(
              'flex items-center justify-center rounded-md transition-all duration-200',
              compact ? 'w-8 h-11' : 'w-11 h-11',
              isActive
                ? 'ring-2 ring-[#9B7B3A] ring-offset-1 ring-offset-[#FAFAF5]'
                : 'opacity-55 hover:opacity-100'
            )}
          >
            <Flag code={code} className={compact ? 'w-5' : 'w-6'} />
          </button>
        );
      })}
    </div>
  );
}
